import { cva } from "class-variance-authority";
import type * as React from "react";

import type { RegisteredComponent } from "@builder.io/sdk-react";

import { cn } from "~/lib/utils";

/**
 * ThemeBox — a themed wrapper `<div>` (SFCC-11736-adjacent). Ported 1:1 from
 * hec-composable `hec/builder/blocks/theme-box/themeBox.jsx`. Composable
 * registers it globally; Workers never ported it, so any Builder entry using a
 * `ThemeBox` block (the HSA `/learning-center` landing + module pages do) hit
 * the SDK's "Could not find a registered component named ThemeBox" warning and
 * rendered empty. Registering it fixes the render + silences the warning.
 *
 * `noWrap` + `canHaveChildren`: the SDK passes the rendered child blocks as
 * `children`; the component owns the root element and spreads `attributes`.
 */
const variant = {
  light: "",
  medium: "theme-medium",
  dark: "theme-dark",
} as const;

const themeVariants = cva("bg-background text-foreground", {
  variants: { variant },
  defaultVariants: { variant: "light" },
});

interface ThemeBoxProps {
  theme?: keyof typeof variant;
  attributes?: Record<string, unknown> & { className?: string };
  children?: React.ReactNode;
}

export const ThemeBox = ({ theme, attributes, children }: ThemeBoxProps) => {
  const { className, ...rest } = attributes ?? {};
  return (
    <div className={cn(themeVariants({ variant: theme }), className)} {...rest}>
      {children}
    </div>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const ThemeBoxDefinition: RegisteredComponent = {
  component: ThemeBox,
  name: "ThemeBox",
  noWrap: true,
  canHaveChildren: true,
  inputs: [
    {
      name: "theme",
      friendlyName: "Theme",
      type: "string",
      enum: Object.keys(variant),
    },
  ],
};
